// Module-level singleton registry of plugin-contributed nodes. Same pattern
// as graphState.js / faceGraphState.js — written once by pluginLoader at
// startup, read by GraphEvaluator (face-graph dispatch fall-through), by
// EventRuntime (event-graph dispatch fall-through), and by the editor
// (NODE_TYPES / EVENT_NODE_TYPES merging).
//
// Never written to from the render loop. Plugins are immutable after load
// for the lifetime of the page.

const nodes = new Map()
const listeners = new Set()
let version = 0

export const pluginNodes = nodes

// Subscribe to registry changes. Returns an unsubscribe fn. Used by
// BlueprintEditor so its `nodeTypes` map is re-derived once plugins finish
// loading after first render — otherwise plugin nodes are in graph state
// but ReactFlow has no renderer for them and renders nothing.
export function subscribePluginRegistry(cb) {
  listeners.add(cb)
  return () => listeners.delete(cb)
}

export function getPluginRegistryVersion() {
  return version
}

// Shape of a registered entry (one per node type the plugin declares):
//   {
//     pluginName: string,        // e.g. 'ogs-branch'
//     nodeManifest: object,      // the entry from plugin.json `nodes[]`
//     instance: object,          // the plugin's exported default object
//   }
//
// Lifecycle the plugin's exported default may implement. The EXECUTION LANE is
// determined by the node's graph + phase (see docs/PLUGINS.md):
//   - init(host, manifest)            once, after the plugin's WASM/JS is ready
//   - tick(weight, data, id, pins)    each frame — body/face, phase 'preMixer'
//   - readPin(pinId, data, id)        pull a dataOut pin's current value (number or view)
//   - deform(frameCtx, data, id)      each frame after mixer.update — phase 'postMixer'
//   - fire(inputs, data, id) [RESERVED] each time an event-graph node executes
//   - emit(ctx)          [RESERVED]   GLSL emit — material graph
//   - inlineUI: { [type]: ReactComponent }   optional custom node-card UI
// init/tick/readPin/deform/inlineUI are wired today; fire/emit are RESERVED
// (validate now, execute in later follow-ups).

export function registerPluginNode(pluginName, nodeManifest, instance) {
  const type = nodeManifest.type
  if (!type || !type.startsWith('plugin.')) {
    throw new Error(`pluginNodeRegistry: invalid node type "${type}" — must start with "plugin."`)
  }
  // Node types are a global namespace. Re-registering the SAME plugin is fine
  // (HMR / React-StrictMode double-effect re-invoke the loader). Two DIFFERENT
  // plugins claiming one type is a collision — fail loud rather than silently
  // letting load order decide the winner.
  const existing = nodes.get(type)
  if (existing && existing.pluginName !== pluginName) {
    throw new Error(`pluginNodeRegistry: node type "${type}" is already registered by plugin "${existing.pluginName}" — types must be globally unique (offending plugin "${pluginName}")`)
  }
  nodes.set(type, { pluginName, nodeManifest, instance })
  version++
  for (const cb of listeners) {
    try { cb() } catch (e) { console.error('[pluginNodeRegistry] listener error', e) }
  }
}

export function getPluginNode(type) {
  return nodes.get(type) ?? null
}

export function listPluginNodesByGraph(graph) {
  return [...nodes.values()].filter(e => e.nodeManifest.graph === graph)
}

if (typeof window !== 'undefined') {
  window.__PLUGIN_NODE_REGISTRY__ = nodes
}
